import { isProAdvantageCustomer, loadProAdvantageScripts } from './utils/pro-advantage-utils';
import { IChatbotConfig, IDefaultConfig, IExperimentConfig, IFeatureFlagConfig } from './interfaces/init-chatbot-interfaces';
import { loadChatbot } from './utils/init-utils/render-chatbot';
import { getInitChatbotConfig } from './utils/init-utils/init-chatbot-utils';
import { getVariation, isFeatureEnabled } from '@vp/ab-reader';
import { loadRegularChat } from './utils/init-utils/load-regular-chat';

/**
 * Initialize the chatbot component in the browser environment
 * @param {ChatbotConfig} config - Configuration options for the chatbot
 * @returns {void}
 */
const initChatbot = (config: IChatbotConfig): void => {
  try {
    if (typeof document === 'undefined' || !document.body) {
      console.error('initChatbot can only be called in a browser environment with a valid document.body.');
      return;
    }
    if (window.location.href.includes('localhost')) {
      loadChatbot(config, false, true, []);
      return;
    }
    if (isProAdvantageCustomer()) {
      console.warn('Chatbot is disabled for ProAdvantage users., loading ProAdvantage scripts instead.');
      loadProAdvantageScripts(config.locale?.toLowerCase());
      return;
    }
    if (config.tenant === 'vcs') {
      loadRegularChat({ locale: config.locale, tenant: config.tenant, pageName: config.pageName || '' });
      return;
    }
    const loadChat = (data: IFeatureFlagConfig | IExperimentConfig | IDefaultConfig) => {
      if (data.deploymentType === 'featureFlag') {
        /** For featureFlag deployment type, check if feature is enabled */
        const isEnabled = isFeatureEnabled(data.deploymentConfig.featureFlagName || '');
        if (isEnabled) {
          loadChatbot(config, data?.disableChatbot, data?.enableChatbotOnAllPages, data?.chatbotEnabledPages, data?.chatbotDisabledPages);
        } else {
          loadRegularChat({ locale: config.locale, tenant: config.tenant, pageName: config.pageName || '' });
        }
      } else if (data.deploymentType === 'experiment') {
        /** For experiment deployment type, check if variation is test variation */
        const variationKey = getVariation(data.deploymentConfig.experimentName || '');
        if (variationKey === data.deploymentConfig.testVariantName) {
          loadChatbot(config, data?.disableChatbot, data?.enableChatbotOnAllPages, data?.chatbotEnabledPages, data?.chatbotDisabledPages);
        } else {
          loadRegularChat({ locale: config.locale, tenant: config.tenant, pageName: config.pageName || '' });
        }
      } else if (data.deploymentType === 'enabled') {
        /** For enabled deployment type, always load chatbot */
        loadChatbot(config, data?.disableChatbot, data?.enableChatbotOnAllPages, data?.chatbotEnabledPages, data?.chatbotDisabledPages);
      } else if (data.deploymentType === 'disabled') {
        /** For disabled deployment type, always initialize chat anywhere */
        loadRegularChat({ locale: config.locale, tenant: config.tenant, pageName: config.pageName || '' });
      }
    };

    getInitChatbotConfig(config.locale?.toLowerCase())
      .then((res) => {
        console.log(res);
        loadChat(res);
      })
      .catch((error) => {
        console.error('Error fetching chatbot configuration:', error);
        if (config.locale === 'en-ca') {
          loadChat({ deploymentType: 'enabled', disableChatbot: false } as IDefaultConfig);
        } else if (config.locale === 'en-us') {
          loadChat({ deploymentType: 'disabled', disableChatbot: true } as IDefaultConfig);
        }
      });
  } catch (error) {
    console.error('Failed to initialize chatbot:', error);
    if (window && window.newrelic && window.newrelic.addPageAction) {
      window.newrelic.addPageAction('ca-chatbot-init-error', { logLevel: 'error', message: error.message, errorType: 'initError' });
    }
  }
};

export { initChatbot };
